import { ReactElement, useContext, useState } from 'react';

import { Field } from 'formik';
import gridStyle from '@pages/forms/Grid.module.scss';
import { Alert, Modal, TextInput } from '@components';
import VicFinesContext from '@pages/forms/VicFinesFormContext';
import InfoIcon from '../../../../images/info.svg';
import QuestionIcon from '../../../../images/question.svg';
import styles from './ReviewStep1.module.scss';

const isSanctioned = 'isSanctioned'; // Error Message 1
const isSeriousESIOffence = 'isSeriousESIOffence'; // Error Message 2
const isIneligibleCode = 'isIneligible'; // Error Message 3
const isNominatedCode = 'IsNominated'; // Error Message 4
const isFullyPaidCode = 'isFullyPaid'; // Error Message 5
const isSevenDayServed = 'isSevenDayServed'; // Error Message 6
const isWarrant = 'isWarrant'; // Error Message 7
const NotInSystem = 'NotInSystem'; // Error Message 7

function ReviewStep1(): ReactElement {
  const { apiError, tooltips } = useContext(VicFinesContext);
  const validTitle = Object.keys(tooltips).filter((v) => v.includes('obligationlocationonformstep1modal'));
  const modalContent = tooltips[validTitle[0]];
  const titleText = validTitle[0] ? validTitle[0].substring(validTitle[0].indexOf('|'), validTitle[0].length).replaceAll('|', ' ') : 'Not sure what to enter?';

  // Error Code 1
  const SanctionedIneligibleAlertContentError1 = (): ReactElement => (
    <>
      <br />
      <p>
        Our records show that a sanction has been applied to this fine.
      </p>
      <br />
      <p>
        <a href="/Your-options/Pay-your-fine" target="_blank" rel="noopener noreferrer">
          Pay your fine
        </a>
      </p>
    </>
  );

// Error Code 2
  const SeriousESIOffenceAlertContentError2 = (): ReactElement => (
    <div>
      <br />
      <p>This fine was issued for an excessive speed, drug or alcohol offence.</p>
      <br />
      <p>
        <a href="/Your-options/Pay-your-fine" target="_blank" rel="noopener noreferrer">
          Pay your fine
        </a>
      </p>
      <br />
      <p>
        You may also call Fines Victoria on
        {' '}
        <a href="tel:0392008111" title="Fines Victoria phone number">
          03 9200 8111
        </a>
        {' '}
        (or
        {' '}
        <a href="tel:1300369819" title="Fines Victoria phone number">
          1300 369 819
        </a>
        {' '}
        for regional callers) to discuss your options.
      </p>
    </div>
  );
  // Error Code 3
  const IneligibleAlertContentError3 = (): ReactElement => (
    <div>
      <br />
      <p>
        <a href="/Your-options/Pay-your-fine" target="_blank" rel="noopener noreferrer">
          Pay your fine
        </a>
      </p>
      <br />
      <p>
        You may also call Fines Victoria on
        {' '}
        <a href="tel:0392008111" title="Fines Victoria phone number">
          03 9200 8111
        </a>
        {' '}
        (or
        {' '}
        <a href="tel:1300369819" title="Fines Victoria phone number">
          1300 369 819
        </a>
        {' '}
        for regional callers) to discuss your options.
      </p>
    </div>
  );
  // Error Code 4 and 5
  const NominatedorFullyPaidIneligibleAlertContentError4 = (): ReactElement => (
    <p>
      The obligation you have entered may be closed or may have been reissued to a nominated third party.
      <br />
      <br />
      Check that you have entered the obligation number correctly and try again.
    </p>
  );

  // Error code 6
  const SevenDayIneligibleAlertContentError6 = (): ReactElement => (
    <>
      <br />
      <p>Our records show that a 7 Day Notice has been served on this fine and it has now expired.</p>
      <br />
      <p>
        Once a 7 Day Notice expires, any fines included in this notice are no longer eligible for an Enforcement Review.
        {' '}
        <a href="/Court-fines/7-Day-Notice" target="_blank" rel="noopener noreferrer">
          More information on 7 Day Notices
        </a>
        .
      </p>
    </>
  );

  // Error code 7
  const WarrantAlertContentError7 = (): ReactElement => (
    <>
      <br />
      <p>Our records show that a warrant has been served on this fine.</p>
      <br />
      <p>
        To avoid Sheriff enforcement action,
        {' '}
        <a href="/Your-options/Pay-your-fine" target="_blank" rel="noopener noreferrer">
          pay your fine
        </a>
        {' '}
        or
        {' '}
        <a href="/Contact-Us" target="_blank" title="Fines Victoria contact us page" rel="noopener noreferrer">
          contact us
        </a>
        .
      </p>
    </>
  );

  // Error code 8
  const NotInSystemAlertContentError8 = (): ReactElement => (
    <>
      <br />
      <p>Please enter the obligation number or infringement notice number. This is usually located at the top of your infringement notice.</p>
    </>
  );

  // For Any other error
  const ReviewStep1AlertContent = (): ReactElement => <p>{`${apiError?.Error}`}</p>;

  const [isOpenHelp, setOpenHelp] = useState(false);
  // const [smallScreen, setMatches] = useState(window.matchMedia('(max-width: 1150px)').matches);

  const closeNotifications = () => {
    setOpenHelp(false);
  };

  const ReviewStep1NoteContent = (): ReactElement => (
    <>
      <p>
        You will need to enter the fine’s obligation number or infringement number, the offence date and some other details about yourself or your vehicle.
      </p>
      <p>You can find this information on the infringement notice.</p>
      <br />
      <p>
        If you cannot locate these details, please contact us on
        {' '}
        <a href="tel:0392008111" title="Fines Victoria phone number">
          03 9200 8111
        </a>
        {' '}
        or
        {' '}
        <a href="tel:1300369819" title="Fines Victoria phone number">
          1300 369 819
        </a>
        {' '}
        for regional callers.
      </p>
    </>
  );

  const notificationInformation = (): ReactElement => (
    // eslint-disable-next-line react/no-danger
    <div className={styles.accordContent} dangerouslySetInnerHTML={{ __html: modalContent }} />
  );

  return (
    <>
      <div className={gridStyle.form}>
        <div className="vfH2">Your fine</div>
        <span className="subHeadingH3">
          At this stage of the application, you only only need to provide details of one of your fines - even if you are dealing with multiple matters.
        </span>
        <Field
          name="OBLIGATION_NUMBER"
          component={TextInput}
          title="Obligation number or infringement number"
          icon={QuestionIcon}
          iconPosition="end"
          iconClick={() => setOpenHelp(true)}
          isMandatory
          autocomplete="off"
        />
        {apiError?.Error === isSanctioned && <Alert icon={InfoIcon} message={<SanctionedIneligibleAlertContentError1 />} title="You cannot request a review for this fine" theme="error" classAlt={styles.boxheight} />}
        {apiError?.Error === isSeriousESIOffence && <Alert icon={InfoIcon} message={<SeriousESIOffenceAlertContentError2 />} title="You cannot request a review for this fine" theme="error" classAlt={styles.boxheight} />}
        {apiError?.Error === isIneligibleCode && <Alert icon={InfoIcon} message={<IneligibleAlertContentError3 />} title="This fine is not eligible for review" theme="error" classAlt={styles.boxheight} />}
        {apiError?.Error === isNominatedCode && <Alert icon={InfoIcon} message={<NominatedorFullyPaidIneligibleAlertContentError4 />} theme="error" classAlt={styles.boxheight} />}
        {apiError?.Error === isFullyPaidCode && <Alert icon={InfoIcon} message={<NominatedorFullyPaidIneligibleAlertContentError4 />} theme="error" classAlt={styles.boxheight} />}
        {apiError?.Error === isSevenDayServed && <Alert icon={InfoIcon} message={<SevenDayIneligibleAlertContentError6 />} title="You cannot request a review for this fine" theme="error" classAlt={styles.boxheight} />}
        {apiError?.Error === isWarrant && <Alert icon={InfoIcon} message={<WarrantAlertContentError7 />} title="You cannot request a review for this fine" theme="error" classAlt={styles.boxheight} />}
        {apiError?.Error === NotInSystem && <Alert icon={InfoIcon} message={<NotInSystemAlertContentError8 />} title="The number you entered is not correct." theme="error" classAlt={styles.boxheight} />}
        {apiError?.Error
        && apiError?.Error !== isIneligibleCode
        && apiError?.Error !== isNominatedCode
        && apiError?.Error !== isSanctioned
        && apiError?.Error !== isFullyPaidCode
        && apiError?.Error !== isSeriousESIOffence
        && apiError?.Error !== isSevenDayServed
        && apiError?.Error !== isWarrant
        && apiError?.Error !== NotInSystem
        && <Alert icon={InfoIcon} message={<ReviewStep1AlertContent />} theme="error" classAlt={styles.boxheight} />}
        {isOpenHelp && <Modal show close={closeNotifications} title={titleText} content={notificationInformation()} />}
        <div>Any fines that have been paid in full cannot be included in a review application.</div>
      </div>
      <Alert message={<ReviewStep1NoteContent />} theme="info" icon={InfoIcon} />
    </>
  );
}

export default ReviewStep1;
